import React, { useEffect, useRef } from 'react';
import { useRegisterNudgeTarget } from '@atlassiansox/nudge-tooltip';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { GET_STARTED_LIST_NUDGE_ID } from '@atlassian/jira-onboarding-core/src/constants.tsx';
import {
	useHasNudgeActive,
	useNudgeStep,
} from '@atlassian/jira-onboarding-quickstart-core/src/services/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { componentWithCondition } from '@atlassian/jira-feature-flagging-utils/src/utils/component-with-condition/index.tsx';
import { ERROR_BOUNDARY_ID_JWM_LIST_TOUR_NUDGE } from '../../../common/constants.tsx';
import type { WrapperProps } from '../../../common/types.tsx';
import { withAsyncNudgeTarget } from '../async-nudge-target/index.tsx';
import { getStartedListNudgeEntrypoint } from './entrypoint.tsx';

const runtimeProps = {};

const GetStartedListNudgeAsyncOld = ({ children }: WrapperProps) => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		getStartedListNudgeEntrypoint,
		{},
	);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		entryPointActions.load();
	}, [entryPointActions]);

	const { ref: getStartedListNudgeRef } = useRegisterNudgeTarget<HTMLDivElement>(
		GET_STARTED_LIST_NUDGE_ID,
		true,
	);

	return (
		<>
			<div ref={getStartedListNudgeRef}>{children}</div>
			<JiraEntryPointContainer
				id={ERROR_BOUNDARY_ID_JWM_LIST_TOUR_NUDGE}
				packageName="jiraListOnboarding"
				entryPointReferenceSubject={entryPointReferenceSubject}
				fallback={<></>}
				runtimeProps={runtimeProps}
			/>
		</>
	);
};

const GetStartedListNudgeAsyncNew = ({ children }: WrapperProps) => {
	const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
		getStartedListNudgeEntrypoint,
		{},
	);

	const [nudgeStep] = useNudgeStep();
	const [hasNudgeActive] = useHasNudgeActive();
	const firstLoadRef = useRef(true);

	// eslint-disable-next-line @atlassian/react-entrypoint/no-load-in-hooks
	useEffect(() => {
		if (firstLoadRef.current && hasNudgeActive && nudgeStep === GET_STARTED_LIST_NUDGE_ID) {
			entryPointActions.load();
		}
	}, [entryPointActions, hasNudgeActive, nudgeStep]);

	const { ref: getStartedListNudgeRef } = useRegisterNudgeTarget<HTMLDivElement>(
		GET_STARTED_LIST_NUDGE_ID,
		true,
	);

	return (
		<>
			<div ref={getStartedListNudgeRef}>{children}</div>
			<JiraEntryPointContainer
				id={ERROR_BOUNDARY_ID_JWM_LIST_TOUR_NUDGE}
				packageName="jiraListOnboarding"
				entryPointReferenceSubject={entryPointReferenceSubject}
				fallback={<></>}
				runtimeProps={runtimeProps}
			/>
		</>
	);
};

export const GetStartedListNudgeAsync = componentWithCondition(
	() => fg('tailored_onboarding_m3_gate'),
	GetStartedListNudgeAsyncNew,
	GetStartedListNudgeAsyncOld,
);

/**
 * New nudge target for NIN introduced for jira_list_nin_merge. This is the same as
 * `GetStartedListNudgeAsync` except it uses a unified pattern for loading the entrypoint content on
 * demand, and requires the `useRegisterNudgeTarget` to be defined on the consumer side.
 */
export const GetStartedListNudge = withAsyncNudgeTarget({
	entryPoint: getStartedListNudgeEntrypoint,
	entryPointParams: {},
	nudgeId: GET_STARTED_LIST_NUDGE_ID,
});
